// THE AREA CLICKED ON THE ISOMETRIC: how far it is signed off on the date the page is on,
// the systems it holds, the schedule activities its sign-offs starred, and who signed.
// This is what the tile panel used to show, asked of an area instead of a cube - the
// sign-off's own unit (owner 2026-11-05: "keep levels, areas and system as it was before").
import React from "react";
import { Button, Tooltip } from "antd";
import { StarFilled } from "@ant-design/icons";
import moment from "moment";
import { BLUE, GOLD, GREY, RING } from "./signoffState";
import { cellsOn, inArea } from "./isoModel";

function Star({ level }) {
  if (level >= GOLD) return <StarFilled style={{ color: RING.tenant }} />;
  if (level >= BLUE) return <StarFilled style={{ color: RING.ownerGc }} />;
  return null;
}

const STATE_WORD = { [GREY]: "Not signed off", [BLUE]: "Owner/GC signed off", [GOLD]: "Contractor signed off" };

export default function AreaPanel({ areaId, areas, levels, tiles, states, rings, at, inScope, onScope, canRequest, onRequest }) {
  const a = (areas || {})[areaId];
  if (!a) return null;
  const list = (tiles && tiles.tiles) || [];
  const levelKey = a.levelKey || "";
  const st = Math.max(states.get(`area|${areaId}`) || GREY, levelKey ? states.get(`level|${levelKey}`) || GREY : GREY);
  // the systems the model carries in this area, with their element counts
  const counts = new Map();
  let elements = 0;
  for (const t of list) {
    for (const g of t.g || []) {
      if (!inArea(areaId)(g)) continue;
      elements += g.n || 0;
      for (const s of g.s || []) counts.set(s, (counts.get(s) || 0) + (g.n || 0));
    }
  }
  const systemState = (sys) => states.get(`system|${areaId}|${String(sys).toLowerCase()}`) || GREY;
  const cellCount = levelKey ? cellsOn(list, levelKey, inArea(areaId)).size : 0;
  // the sign-offs that cover this area, newest first
  const holding = (rings || []).filter((r) => r.type === "signoff" && r.target && new Date(r.at) <= at
    && ((r.target.kind === "area" && String(r.target.areaSetId) === String(areaId))
      || (r.target.kind === "system" && String(r.target.areaSetId) === String(areaId))
      || (r.target.kind === "level" && r.target.levelKey === levelKey)));
  const last = holding.find((r) => !r.withdrawn);
  const acts = [];
  const seen = new Set();
  for (const r of holding) (r.activities || []).forEach((n) => { if (!seen.has(n)) { seen.add(n); acts.push({ name: n, at: r.at, level: r.level }); } });

  return (
    <div className="signoff-area-panel" data-testid="signoff-area-panel">
      <div className="signoff-panel-title">{a.name}{a.level ? ` · ${a.level}` : ""}</div>
      <div className={`tile-share ${st > GREY ? "" : "is-partial"}`}>{STATE_WORD[st]} <Star level={st} /></div>
      <div className="tile-row"><span>Placed elements</span><b>{elements.toLocaleString()}</b></div>
      <div className="tile-row"><span>Footprint</span><b>{cellCount.toLocaleString()} tiles</b></div>
      {[...counts.entries()].sort((x, y) => y[1] - x[1]).slice(0, 12).map(([sys, n]) => (
        <div key={sys} className="tile-row"><span>{sys} <Star level={systemState(sys)} /></span><span>{n.toLocaleString()}</span></div>
      ))}
      {acts.length ? (
        <>
          <div className="tile-section">Schedule activities</div>
          {acts.slice(0, 8).map((x) => (
            <div key={x.name} className="tile-row"><span>{x.name}</span><span><StarFilled style={{ color: RING[x.level] }} /> {moment(x.at).format("MMM D")}</span></div>
          ))}
          {acts.length > 8 ? <small className="signoff-muted">+{acts.length - 8} more</small> : null}
        </>
      ) : null}
      {last ? (
        <>
          <div className="tile-section">Approved parties · {last.target.label}, {moment(last.at).format("MMM D")}</div>
          {(last.parties || []).map((p, i) => (
            <div key={i} className="tile-party">
              <div><b>{p.name || p.email}</b><small className="d-block signoff-muted">{[p.company, p.role].filter(Boolean).join(" · ")}</small></div>
              <div className={p.pending ? "text-warning" : ""}>
                {p.signed ? <><StarFilled style={{ color: RING[p.star] }} /> {moment(p.at).format("MMM D, h:mm A")}</>
                  : p.pending ? `Pending · link expires ${moment(p.expiresAt).format("MMM D")}` : p.outcome === "changes" ? "Asked for changes" : ""}
              </div>
            </div>
          ))}
        </>
      ) : null}
      <div className="d-flex flex-wrap mt-2" style={{ gap: 8 }}>
        <Tooltip title={inScope ? "Take this area out of the scope above" : "Add this area to the scope above"}>
          <Button size="small" onClick={() => onScope(areaId)} data-testid="area-scope">{inScope ? "Out of scope" : "Add to scope"}</Button>
        </Tooltip>
        {canRequest ? (
          <Tooltip title="Ask for this area to be signed off">
            <Button size="small" type="primary" onClick={() => onRequest(areaId)} data-testid="area-request">Request sign-off</Button>
          </Tooltip>
        ) : null}
      </div>
    </div>
  );
}
